import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchTrialSets,
  fetchRuns,
  fetchReport,
  type TrialSetSummary,
  type RunView,
  type ScoreReport,
  type ArmScore,
} from '../api/skillTrials';

const pct = (x: number) => `${Math.round(x * 100)}%`;

/**
 * The §8 report: precision/recall per cell, per population, per arm. Deliberately a
 * plain table, not a dashboard (§10.8). Adjudicated and blind are separate blocks
 * and never pooled (§8.1); adjudicated recall is flagged optimistic; blind recall is
 * the estimate. No F-score.
 */
export default function TrialReport() {
  const { t } = useTranslation();
  const [sets, setSets] = useState<TrialSetSummary[]>([]);
  const [setId, setSetId] = useState('');
  const [runs, setRuns] = useState<RunView[]>([]);
  const [runA, setRunA] = useState('');
  const [runB, setRunB] = useState('');
  const [report, setReport] = useState<ScoreReport | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTrialSets().then((l) => {
      setSets(l);
      if (l[0]) setSetId(l[0].id);
    }).catch((e) => setError(e?.message ?? ''));
  }, []);

  useEffect(() => {
    if (!setId) return;
    fetchRuns(setId).then((l) => {
      setRuns(l);
      setRunA(l[0]?.id ?? '');
      setRunB(l[1]?.id ?? '');
    }).catch((e) => setError(e?.message ?? ''));
  }, [setId]);

  const load = useCallback(async (a: string, b: string) => {
    if (!a || !b || a === b) { setReport(null); return; }
    try {
      setError('');
      setReport(await fetchReport(a, b));
    } catch (e) {
      setReport(null);
      setError(e instanceof Error ? e.message : t('skillTrials.report.error'));
    }
  }, [t]);

  useEffect(() => { void load(runA, runB); }, [runA, runB, load]);

  const block = (origin: 'ADJUDICATED' | 'BLIND') =>
    report?.cells.filter((c) => c.origin === origin) ?? [];

  return (
    <div className="mx-auto max-w-[880px] px-4 py-6" style={{ color: '#0f172a' }}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('skillTrials.report.title')}</h1>
          <p className="mt-0.5 text-sm text-slate-500">{t('skillTrials.report.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Sel value={setId} onChange={setSetId} opts={sets.map((s) => ({ v: s.id, l: s.label }))} label={t('skillTrials.report.set')} />
          <Sel value={runA} onChange={setRunA} opts={runs.map((r) => ({ v: r.id, l: r.label }))} label="A" />
          <Sel value={runB} onChange={setRunB} opts={runs.map((r) => ({ v: r.id, l: r.label }))} label="B" />
        </div>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {!report ? (
        <div className="rounded-[14px] border bg-white py-12 text-center text-sm text-slate-500 shadow-sm" style={{ borderColor: '#e2e8f0' }}>
          {t('skillTrials.report.pickTwo')}
        </div>
      ) : (
        <>
          <Block
            title={t('skillTrials.report.adjudicated')}
            note={t('skillTrials.report.adjNote')}
            cells={block('ADJUDICATED')}
            report={report}
            optimisticRecall
          />
          <Block
            title={t('skillTrials.report.blind')}
            note={t('skillTrials.report.blindNote')}
            cells={block('BLIND')}
            report={report}
          />
          <p className="mt-4 text-[11px] text-slate-400">{t('skillTrials.report.footer')}</p>
        </>
      )}
    </div>
  );
}

function Block({
  title, note, cells, report, optimisticRecall,
}: {
  title: string; note: string; cells: ScoreReport['cells']; report: ScoreReport; optimisticRecall?: boolean;
}) {
  const { t } = useTranslation();
  const order = ['REQUIREMENT', 'RESPONSIBILITY', 'ALL'];
  const sorted = [...cells].sort((a, b) => order.indexOf(a.cell) - order.indexOf(b.cell));
  return (
    <div className="mb-5 overflow-hidden rounded-[14px] border bg-white shadow-sm" style={{ borderColor: '#e2e8f0' }}>
      <div className="flex items-baseline justify-between border-b px-4 py-2.5" style={{ borderColor: '#e2e8f0', background: '#f8fafc' }}>
        <span className="text-[13px] font-semibold">{title}</span>
        <span className="text-[11px] text-slate-400">{note}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400">
              <th className="px-4 py-2 text-left font-semibold">{t('skillTrials.report.cell')}</th>
              <ArmHead label={report.run_a_label} optimisticRecall={optimisticRecall} />
              <ArmHead label={report.run_b_label} optimisticRecall={optimisticRecall} />
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => (
              <tr key={c.cell} className="border-t" style={{ borderColor: '#eef2f6' }}>
                <td className="px-4 py-2 font-mono text-[11px] text-slate-600">{c.cell}</td>
                <ArmCells score={c.arm_a} />
                <ArmCells score={c.arm_b} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArmHead({ label, optimisticRecall }: { label: string; optimisticRecall?: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <th className="border-l px-3 py-2 text-right font-mono font-semibold text-slate-500" style={{ borderColor: '#eef2f6' }}>
        {label} · {t('skillTrials.report.p')}
      </th>
      <th className="px-3 py-2 text-right font-mono font-semibold text-slate-500">
        {t('skillTrials.report.r')}{optimisticRecall ? '*' : ''}
      </th>
      <th className="px-3 py-2 text-right font-mono font-semibold text-slate-500">{t('skillTrials.report.f')}</th>
    </>
  );
}

function ArmCells({ score }: { score: ArmScore }) {
  const empty = score.gold_concepts === 0 && score.produced === 0;
  return (
    <>
      <td className="border-l px-3 py-2 text-right tabular-nums" style={{ borderColor: '#eef2f6' }}>
        {empty ? '—' : pct(score.precision)}
        <span className="ml-1 font-mono text-[10px] text-slate-400">{empty ? '' : `${score.produced_hits}/${score.produced}`}</span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {score.gold_concepts === 0 ? '—' : pct(score.recall)}
        <span className="ml-1 font-mono text-[10px] text-slate-400">{score.gold_concepts === 0 ? '' : `${score.gold_hits}/${score.gold_concepts}`}</span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-500">
        {score.facet_accuracy == null ? '—' : pct(score.facet_accuracy)}
      </td>
    </>
  );
}

function Sel({ value, onChange, opts, label }: { value: string; onChange: (v: string) => void; opts: { v: string; l: string }[]; label: string }) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
      <select className="rounded-lg border px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" style={{ borderColor: '#cbd5e1' }} value={value} onChange={(e) => onChange(e.target.value)} disabled={opts.length === 0}>
        {opts.length === 0 && <option value="">—</option>}
        {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
